// Gestão DNA · Painel da Turma — notas, faltas e recuperação na mesma visão.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  ArrowUpRight, CalendarX, ClipboardList, LifeBuoy, Loader2, ShieldAlert, Users,
} from 'lucide-react';
import api from '@/lib/api';

const corNota = (v, max = 10) => {
  if (v === null || v === undefined) return 'text-slate-300';
  if (v < max * 0.6) return 'text-rose-600 font-bold';
  if (v < max * 0.7) return 'text-amber-600 font-semibold';
  return 'text-slate-700';
};

function Resumo({ icon: Icon, label, value, tone }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-3xl border border-slate-200/80 bg-white p-5"
      data-testid={`turma-resumo-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className={`mb-4 inline-flex h-10 w-10 items-center justify-center rounded-2xl ${tone}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="text-3xl font-extrabold tracking-tight text-slate-900">{value ?? '—'}</div>
      <div className="mt-1 text-sm font-semibold text-slate-600">{label}</div>
    </motion.div>
  );
}

export default function GestaoTurmas() {
  const navigate = useNavigate();
  const [turmas, setTurmas] = useState([]);
  const [turmaId, setTurmaId] = useState('');
  const [periodo, setPeriodo] = useState('');
  const [painel, setPainel] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/pedagogico/turmas');
        setTurmas(data.items || []);
        if ((data.items || []).length) setTurmaId(data.items[0].id);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Erro ao carregar turmas');
      }
    })();
  }, []);

  const load = useCallback(async () => {
    if (!turmaId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/pedagogico/turmas/${turmaId}/painel`, {
        params: { period: periodo || undefined },
      });
      setPainel(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao carregar o painel da turma');
      setPainel(null);
    } finally { setLoading(false); }
  }, [turmaId, periodo]);

  useEffect(() => { load(); }, [load]);

  const disciplinas = useMemo(() => painel?.disciplinas || [], [painel]);
  const alunos = painel?.alunos || [];

  return (
    <div className="mx-auto max-w-[1400px] space-y-6" data-testid="gestao-turmas">
      <header>
        <p className="text-sm font-semibold text-violet-600">Visão consolidada</p>
        <h1 className="mt-1 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">Painel da Turma</h1>
        <p className="mt-2 max-w-2xl text-base text-slate-500">
          Notas por disciplina, faltas e alunos em recuperação na mesma tela. Clique em um aluno
          para abrir a ficha completa.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <select value={turmaId} onChange={(e) => setTurmaId(e.target.value)}
          className="rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 outline-none focus:border-violet-300 focus:ring-4 focus:ring-violet-100"
          data-testid="turma-select">
          {turmas.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}{t.period ? ` · ${t.period}` : ''}{t.year ? ` · ${t.year}` : ''}
            </option>
          ))}
        </select>
        <select value={periodo} onChange={(e) => setPeriodo(e.target.value)}
          className="rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 outline-none focus:border-violet-300 focus:ring-4 focus:ring-violet-100"
          data-testid="turma-periodo">
          <option value="">Todos os períodos</option>
          {(painel?.periodos || []).map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-violet-500" />}
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Resumo icon={Users} label="Alunos" value={painel?.resumo?.alunos} tone="bg-violet-50 text-violet-700" />
        <Resumo icon={ClipboardList} label="Média da turma" value={painel?.resumo?.media_turma} tone="bg-indigo-50 text-indigo-700" />
        <Resumo icon={LifeBuoy} label="Em recuperação" value={painel?.resumo?.em_recuperacao} tone="bg-amber-50 text-amber-700" />
        <Resumo icon={CalendarX} label="Faltas no ano" value={painel?.resumo?.faltas_total} tone="bg-rose-50 text-rose-700" />
      </div>

      <div className="overflow-hidden rounded-3xl border border-slate-200/80 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] border-collapse" data-testid="turma-tabela">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/60">
                <th className="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400">Aluno</th>
                {disciplinas.map((d) => (
                  <th key={d} className="px-3 py-3 text-center text-[10px] font-bold uppercase tracking-wider text-slate-400">{d}</th>
                ))}
                <th className="px-3 py-3 text-center text-[10px] font-bold uppercase tracking-wider text-slate-400">Média</th>
                <th className="px-3 py-3 text-center text-[10px] font-bold uppercase tracking-wider text-slate-400">Faltas</th>
                <th className="px-3 py-3 text-center text-[10px] font-bold uppercase tracking-wider text-slate-400">Recuperação</th>
                <th className="px-3 py-3 text-center text-[10px] font-bold uppercase tracking-wider text-slate-400">Ocorr.</th>
              </tr>
            </thead>
            <tbody>
              {alunos.map((a) => (
                <tr key={a.student_id}
                  onClick={() => navigate(`/gestao/alunos/${a.student_id}`)}
                  className="cursor-pointer border-b border-slate-50 transition-colors last:border-0 hover:bg-violet-50/40"
                  data-testid={`turma-aluno-${a.student_id}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-slate-800">{a.name}</span>
                      <ArrowUpRight className="h-3.5 w-3.5 text-slate-300" />
                    </div>
                    {a.registration_number && (
                      <span className="text-xs text-slate-400">mat. {a.registration_number}</span>
                    )}
                  </td>
                  {disciplinas.map((d) => (
                    <td key={d} className={`px-3 py-3 text-center text-sm ${corNota(a.medias?.[d])}`}>
                      {a.medias?.[d] ?? '—'}
                    </td>
                  ))}
                  <td className={`px-3 py-3 text-center text-sm ${corNota(a.media_geral)}`}>{a.media_geral ?? '—'}</td>
                  <td className="px-3 py-3 text-center text-sm">
                    <span className={a.faltas > 0 ? 'font-bold text-rose-600' : 'text-slate-400'}>{a.faltas}</span>
                  </td>
                  <td className="px-3 py-3 text-center">
                    {a.recuperacao?.length ? (
                      <span className="inline-flex flex-wrap justify-center gap-1">
                        {a.recuperacao.map((d) => (
                          <span key={d} className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-700">{d}</span>
                        ))}
                      </span>
                    ) : <span className="text-xs text-emerald-600">em dia</span>}
                  </td>
                  <td className="px-3 py-3 text-center text-sm">
                    {a.ocorrencias > 0 ? (
                      <span className="inline-flex items-center gap-1 font-bold text-violet-700">
                        <ShieldAlert className="h-3.5 w-3.5" />{a.ocorrencias}
                      </span>
                    ) : <span className="text-slate-300">0</span>}
                  </td>
                </tr>
              ))}
              {!loading && alunos.length === 0 && (
                <tr><td colSpan={disciplinas.length + 5} className="py-14 text-center text-sm text-slate-400">
                  Nenhum aluno com registros nesta turma.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
